import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';
import { useCurrency } from '../context/CurrencyContext';
import { useMonth } from '../context/MonthContext';
import { formatDate, getDayName, getTodayDate } from '../utils/date';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Search, Download } from 'lucide-react';
import { useDataRefresh } from '../context/DataContext';

export const ExpensesPage = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const { triggerRefresh } = useDataRefresh();
  const { formatCurrency } = useCurrency();
  const { selectedMonth } = useMonth();
  
  // User's categories from Settings
  const [userCategories, setUserCategories] = useState([]);
  
  // Use refs to prevent input focus loss
  const descriptionInputRef = useRef(null);
  const categoryInputRef = useRef(null);
  
  const [filters, setFilters] = useState({
    search: '',
    category_name: ''
  });

  // Form state with proper initialization using local date
  const getInitialFormData = useCallback(() => ({
    date: getTodayDate(),
    day: getDayName(getTodayDate()),
    amount: '',
    description: '',
    category_name: '',
    notes: '',
    payment_method: 'Cash',
    reference_number: ''
  }), []);

  const [formData, setFormData] = useState(getInitialFormData);

  const [categories, setCategories] = useState([]);

  // Fetch user's categories from Settings
  const fetchUserCategories = async () => {
    try {
      const response = await api.get('/categories');
      setUserCategories(response.data || []);
    } catch (error) {
      console.error('Failed to fetch user categories:', error);
    }
  };

  useEffect(() => {
    fetchExpenses();
    fetchCategories();
    fetchUserCategories();
  }, [selectedMonth, filters.search, filters.category_name]);

  const fetchExpenses = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const params = { month: selectedMonth };
      if (filters.search) params.search = filters.search;
      if (filters.category_name) params.category_name = filters.category_name;

      const response = await api.get('/expenses', { params });
      setExpenses(response.data);
    } catch (error) {
      toast.error('Failed to fetch expense data');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/suggestions/categories');
      setCategories(response.data.suggestions);
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.amount || formData.amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    try {
      setSaving(true);
      console.log('Submitting expense:', editingItem ? 'UPDATE' : 'CREATE', formData);
      
      if (editingItem) {
        const response = await api.put(`/expenses/${editingItem.id}`, formData);
        console.log('Update response:', response);
      } else {
        const response = await api.post('/expenses', formData);
        console.log('Create response:', response);
      }
      
      // Await the fetch to ensure data is refreshed before closing dialog
      await fetchExpenses(false);
      triggerRefresh(); // Notify other components (Dashboard) to refresh
      
      setDialogOpen(false);
      resetForm();
      toast.success(editingItem ? 'Expense entry updated successfully' : 'Expense entry added successfully');
    } catch (error) {
      console.error('Submit error:', error);
      console.error('Error response:', error.response);
      toast.error(error.response?.data?.detail || 'Failed to save expense entry');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    // Ensure date is properly formatted
    const editDate = item.date ? item.date.split('T')[0] : getTodayDate();
    setFormData({
      date: editDate,
      day: getDayName(editDate),
      amount: item.amount,
      description: item.description || '',
      category_name: item.category_name || '',
      notes: item.notes || '',
      payment_method: item.payment_method || 'Cash',
      reference_number: item.reference_number || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!itemToDelete || !itemToDelete.id) {
      toast.error('Invalid item selected');
      return;
    }
    
    try {
      console.log('Deleting expense entry:', itemToDelete.id);
      const response = await api.delete(`/expenses/${itemToDelete.id}`);
      console.log('Delete response:', response);
      toast.success('Expense entry deleted successfully');
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchExpenses();
      triggerRefresh(); // Notify other components (Dashboard) to refresh
    } catch (error) {
      console.error('Delete error:', error);
      console.error('Error response:', error.response);
      toast.error(error.response?.data?.detail || 'Failed to delete expense entry');
    }
  };

  const resetForm = useCallback(() => {
    setEditingItem(null);
    setFormData(getInitialFormData());
  }, [getInitialFormData]);

  const handleDateChange = useCallback((date) => {
    setFormData(prev => ({
      ...prev,
      date,
      day: getDayName(date)
    }));
  }, []);

  // Stable handler for text inputs to prevent focus loss
  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Handle select without affecting text input
  const handleSelectChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleExport = async () => {
    try {
      const response = await api.get('/export/expenses', {
        params: { month: filters.month },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `expenses_${filters.month}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Expense data exported successfully');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const totalExpenses = expenses.reduce((sum, item) => sum + item.amount, 0);

  return (
    <div>
      <div className="mb-8">
        <div className="flex flex-col gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl tracking-tight font-light text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Expenses
            </h1>
            <p className="mt-2 text-sm sm:text-base leading-relaxed text-slate-600">
              Manage and track your expense entries
            </p>
          </div>

          <div className="flex justify-end">
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button
                  className="bg-red-600 text-white hover:bg-red-700 rounded-xl px-4 sm:px-6 py-2.5 text-sm transition-all hover:-translate-y-0.5"
                  data-testid="add-expense-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Expense
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>{editingItem ? 'Edit Expense Entry' : 'Add New Expense'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto space-y-4 pr-1 -mr-1">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="date">Date</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleDateChange(e.target.value)}
                    className="rounded-xl bg-slate-50"
                    data-testid="expense-date-input"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="day">Day</Label>
                  <Input
                    id="day"
                    value={formData.day}
                    readOnly
                    className="rounded-xl bg-slate-100"
                    data-testid="expense-day-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="amount">Amount</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => handleInputChange('amount', e.target.value)}
                  placeholder="500"
                  className="rounded-xl bg-slate-50"
                  data-testid="expense-amount-input"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Input
                  ref={descriptionInputRef}
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="What did you spend on?"
                  className="rounded-xl bg-slate-50"
                  data-testid="expense-description-input"
                  autoComplete="off"
                  required
                />
              </div>

              <div>
                <Label htmlFor="category_name">Category</Label>
                {/* User categories from Settings */}
                {userCategories.length > 0 && (
                  <>
                    <Select
                      key="category-settings-select"
                      value={userCategories.some(c => c.name === formData.category_name) ? formData.category_name : undefined}
                      onValueChange={(value) => handleSelectChange('category_name', value)}
                    >
                      <SelectTrigger className="rounded-xl bg-slate-50 mt-1.5" data-testid="expense-category-select">
                        <SelectValue placeholder="Select from your categories" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {userCategories.filter(c => c.is_active).map((category) => (
                          <SelectItem key={category.id} value={category.name}>
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">Or type a new category below</p>
                  </>
                )}
                {/* Show suggestions if no user categories */}
                {userCategories.length === 0 && categories.length > 0 && (
                  <>
                    <Select
                      key="category-suggest-select"
                      value={categories.includes(formData.category_name) ? formData.category_name : undefined}
                      onValueChange={(value) => handleSelectChange('category_name', value)}
                    >
                      <SelectTrigger className="rounded-xl bg-slate-50 mt-1.5" data-testid="expense-category-select">
                        <SelectValue placeholder="Select from existing categories" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {categories.map((category) => (
                          <SelectItem key={category} value={category}>
                            {category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">Or type a new category below</p>
                  </>
                )}
                <Input
                  ref={categoryInputRef}
                  id="category_name"
                  value={formData.category_name}
                  onChange={(e) => handleInputChange('category_name', e.target.value)}
                  placeholder="Type category name"
                  className="rounded-xl bg-slate-50 mt-2"
                  data-testid="expense-category-input"
                  autoComplete="off"
                />
              </div>

              <div>
                <Label htmlFor="payment_method">Payment Method</Label>
                <Select
                  value={formData.payment_method}
                  onValueChange={(value) => handleSelectChange('payment_method', value)}
                >
                  <SelectTrigger className="rounded-xl bg-slate-50" data-testid="expense-payment-select">
                    <SelectValue placeholder="Select payment method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="GCash">GCash</SelectItem>
                    <SelectItem value="Card">Card</SelectItem>
                    <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="reference_number">Reference Number (Optional)</Label>
                <Input
                  id="reference_number"
                  value={formData.reference_number}
                  onChange={(e) => handleInputChange('reference_number', e.target.value)}
                  placeholder="REF-001"
                  className="rounded-xl bg-slate-50"
                  data-testid="expense-reference-input"
                  autoComplete="off"
                />
              </div>

              <div>
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Input
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Additional notes"
                  className="rounded-xl bg-slate-50"
                  data-testid="expense-notes-input"
                  autoComplete="off"
                />
              </div>
              </div>

              <DialogFooter className="flex-shrink-0 gap-2 pt-4 mt-4 border-t border-slate-100">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setDialogOpen(false);
                    resetForm();
                  }}
                  className="rounded-xl flex-1 sm:flex-none"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-red-600 text-white hover:bg-red-700 rounded-xl disabled:opacity-70 flex-1 sm:flex-none"
                  data-testid="expense-submit-button"
                >
                  {saving ? 'Saving...' : (editingItem ? 'Update' : 'Add')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-2xl p-6 border border-slate-200" data-testid="total-expenses-summary">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-400 mb-2">Total Expenses</p>
          <p className="text-2xl font-medium text-red-600" style={{ fontFamily: 'Outfit, sans-serif' }}>{formatCurrency(totalExpenses)}</p>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-slate-200" data-testid="expense-count-summary">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-400 mb-2">Total Transactions</p>
          <p className="text-2xl font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{expenses.length}</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-4 sm:p-6 md:p-8 border border-slate-200 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.05)]">
        <div className="flex flex-col gap-4 mb-6">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search expense entries..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10 rounded-xl bg-slate-50 text-sm"
                data-testid="expense-search-input"
              />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <Button
              onClick={handleExport}
              variant="outline"
              className="rounded-xl text-sm whitespace-nowrap"
              data-testid="export-expense-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin mx-auto"></div>
            <p className="mt-4 text-slate-600 text-sm">Loading expense data...</p>
          </div>
        ) : expenses.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-500 text-sm">No expense entries found</p>
          </div>
        ) : (
          <div className="overflow-x-auto -mx-2 sm:mx-0">
            <div className="inline-block min-w-full align-middle">
              <table className="w-full" data-testid="expense-table">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-4 px-2 text-xs uppercase tracking-[0.2em] font-semibold text-slate-400">Date</th>
                  <th className="text-right py-4 px-2 text-xs uppercase tracking-[0.2em] font-semibold text-slate-400">Amount</th>
                  <th className="text-left py-4 px-2 text-xs uppercase tracking-[0.2em] font-semibold text-slate-400">Description</th>
                  <th className="text-left py-4 px-2 text-xs uppercase tracking-[0.2em] font-semibold text-slate-400">Category</th>
                  <th className="text-left py-4 px-2 text-xs uppercase tracking-[0.2em] font-semibold text-slate-400">Payment</th>
                  <th className="text-right py-4 px-2 text-xs uppercase tracking-[0.2em] font-semibold text-slate-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((item) => (
                  <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid="expense-row">
                    <td className="py-4 px-2 text-sm text-slate-700">{formatDate(item.date)}</td>
                    <td className="py-4 px-2 text-right text-sm font-medium text-red-600">{formatCurrency(item.amount)}</td>
                    <td className="py-4 px-2 text-sm text-slate-900">{item.description}</td>
                    <td className="py-4 px-2">
                      <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                        {item.category_name}
                      </span>
                    </td>
                    <td className="py-4 px-2 text-sm text-slate-700">{item.payment_method}</td>
                    <td className="py-4 px-2 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          onClick={() => handleEdit(item)}
                          variant="ghost"
                          size="sm"
                          className="text-slate-600 hover:text-slate-900"
                          data-testid="expense-edit-button"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => {
                            setItemToDelete(item);
                            setDeleteDialogOpen(true);
                          }}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          data-testid="expense-delete-button"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this expense entry. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
